import { Dialog } from "@mui/material";
import { FunctionComponent, useCallback } from "react";
import './ChatAttachmentPopup.scss';
import images from "../../../assets/images";
import { useDropzone } from "react-dropzone";
import ToasterWithMessage from "../../toaster";
import { EditFileType } from "../../../pages/CommonChats/components/CommonChatBox";
import { getScreenDimensions } from "../../../utils/helper";

interface attachmentProp {
    open: boolean;
    handleClose: () => void;
    setUploadFileData: (file: File | null) => void;
    uploadFileData:File | null;
    editFile: EditFileType | null;
    handleConfirmation: (file:File) => void;
    setUploadLoading: (data:boolean) => void;
    uploadLoading: boolean;
}

const ChatAttachmentPopup: FunctionComponent<attachmentProp> = ({ open, handleClose,setUploadFileData, uploadFileData, editFile, handleConfirmation, uploadLoading, setUploadLoading}) => {
    const { chatDocument, chatImage, PopupCloseIcon } = images;
    const { error } = ToasterWithMessage();
    const { isMobileScreen } = getScreenDimensions();

// Function to handle file drops and validations
const onDrop = useCallback((acceptedFiles: File[], fileRejections: any, fileType: "document" | "media") => {
    if (fileRejections.length > 0) {
        // Handle errors using a toaster
        fileRejections.forEach((rejection: any) => {
            rejection.errors.forEach((err: any) => {
                if (err.code === "file-too-large") {
                    if (fileType === "document") {
                        error("File size must be less than or equal to 25 MB");
                    }
                } else if (err.code === "file-invalid-type") {
                    if (fileType === "document") {
                        error("Please select pdf, csv or word file");
                    } else {
                        error("Please select either jpg, jpeg, png, svg image or mp4 video");
                    }
                } else {
                    error(err.message); // Generic error message for other cases
                }
            });
        });
        return;
    }

    if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles[0];
        const acceptedFileType = acceptedFile?.type;
        if(acceptedFileType.startsWith('image/')){
            if (acceptedFile?.size > 5 * 1024 * 1024) { // 5 MB for images
                error("Image size must be less than or equal to 5 MB");
                return;
            }
        }else if(acceptedFileType === 'video/mp4'){
            if (acceptedFile?.size > 200 * 1024 * 1024) { // 200 MB for videos
                error("Video size must be less than or equal to 200 MB");
                return;
            }
        }
        if (uploadFileData !== null || editFile) {
            handleConfirmation(acceptedFiles[0]);
            handleClose();
        } else {
            setUploadFileData(acceptedFiles[0]);
            handleClose();
        }
    }
}, [setUploadFileData, handleClose, handleConfirmation, uploadFileData, error]);


    // Configuration for the image/video dropzone
    const { getRootProps: getImageVideoRootProps, getInputProps: getImageVideoInputProps } = useDropzone({
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/svg+xml': [],
            'image/jpg': [],
            'video/mp4': [],
        },
        onDrop: (acceptedFiles, fileRejections) => onDrop(acceptedFiles, fileRejections, "media"),
        multiple: false
    });

    // Configuration for the document dropzone
    const { getRootProps: getDocumentRootProps, getInputProps: getDocumentInputProps } = useDropzone({
        accept: {
            "text/csv": [],
            "application/pdf": [],
            "application/msword": [],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "application/vnd.ms-excel": [],
            "application/vnd.ms-powerpoint": [],
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": [],
            "application/epub+zip": []
        },
        maxSize: 25 * 1024 * 1024, // 25 MB for documents
        onDrop: (acceptedFiles, fileRejections) => onDrop(acceptedFiles, fileRejections, "document"),
        multiple: false
    });

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        borderRadius: '30px'
                    }
                }}
            >
                <img src={PopupCloseIcon} alt="popupcloseicon" className="attachmentCloseBtn" onClick={handleClose}/>
                <div className="chatAttachmentCtn">
                    {/* Dropzone for Images & Videos */}
                    <div className="fileUploadCtn" {...getImageVideoRootProps()} style={{ padding: `${isMobileScreen ? '10px' : '20px'}`, textAlign: 'center', cursor: 'pointer' }}>
                        <input {...getImageVideoInputProps()} />
                        <div className="videoImageCtn"><img src={chatImage} alt="videoImage" /></div>
                        <span className="attachmentContent">Photos & Videos</span>
                    </div>

                    {/* Dropzone for Documents */}
                    <div className="fileUploadCtn" {...getDocumentRootProps()} style={{ padding: `${isMobileScreen ? '10px' : '20px'}`, textAlign: 'center', cursor: 'pointer' }}>
                        <input {...getDocumentInputProps()} />
                        <div className="videoImageCtn"><img src={chatDocument} alt="document" /></div>
                        <span className="attachmentContent">Document</span>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default ChatAttachmentPopup;
